import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Music24Capabilities } from 'fym-common/src/license/music24Plans';
import { TypedMemo } from '../../common/types';
import { Music24FeatureRow, featureRowNoneText } from '../../music24/config/music24PlanFeatureRows';
import { LockedControl } from '../../music24/components/LockedControl/LockedControl';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../music24/hooks/useUpgradePrompt';
import { SubscriptionRowUsage } from './useSubscriptionUsage';
/* eslint-disable css-modules/no-unused-class -- shared with SubscriptionFeaturesList.tsx; each file uses its own slice of it */
import styles from './SubscriptionFeaturesList.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SubscriptionFeatureRowProps {
  row: Music24FeatureRow;
  capabilities: Music24Capabilities;
  /** From `useSubscriptionUsage`, for the three rows a client endpoint can count today. */
  usage?: SubscriptionRowUsage;
  /** The upgrade feature this row locks to, when the plan doesn't include it — `undefined` for a
   * row that's never "not included" (a number, or a value that's always present in some form). */
  lockFeature?: SMLUpgradeFeature;
}

/**
 * One row of design r1's "What your plan includes": the label, an optional usage meter (pages,
 * artists, seats), and the plan's own value — or a lock chip when the plan doesn't include it.
 */
const SubscriptionFeatureRowComponent: React.FC<SubscriptionFeatureRowProps> = ({
  row,
  capabilities,
  usage,
  lockFeature,
}) => {
  const intl = useIntl();
  const openUpgradePrompt = useUpgradePrompt();
  const label = intl.formatMessage(row.label);
  const value = row.value(capabilities, intl);
  const isLocked = lockFeature !== undefined && value === featureRowNoneText(intl);
  const onLockClick = useCallback(() => {
    if (lockFeature !== undefined) {
      openUpgradePrompt(lockFeature);
    }
  }, [openUpgradePrompt, lockFeature]);

  const percent = useMemo(() => {
    if (usage === undefined || usage.used === undefined || usage.limit <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((usage.used / usage.limit) * 100));
  }, [usage]);

  return (
    <div className={styles.row}>
      <span>{label}</span>
      <div className={styles.usage}>
        {usage !== undefined && usage.used !== undefined && (
          <>
            <span className={styles.usageFig}>
              {intl.formatMessage(
                { id: 'app.sml.settings.subscription.rowUsage', defaultMessage: '{used} of {limit}' },
                { used: usage.used, limit: usage.limit }
              )}
            </span>
            <div
              className={clsx(styles.meter, usage.used >= usage.limit && styles.meterFull)}
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <span style={{ width: `${percent}%` }} />
            </div>
          </>
        )}
      </div>
      {isLocked ? (
        <LockedControl
          variant="chip"
          feature={lockFeature as SMLUpgradeFeature}
          label={label}
          onClick={onLockClick}
          className={styles.valueSlot}
        />
      ) : (
        <span className={styles.value}>{value}</span>
      )}
    </div>
  );
};

export const SubscriptionFeatureRow = TypedMemo(SubscriptionFeatureRowComponent);
